import React from "react";
import type { ApolloClient } from "@apollo/client";
import { isEmpty } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import IconSpinner from "components/IconSpinner";

import {
  addPaycheckContributionSource,
  editPaycheckContributionSource,
  getContributionAbilityAndEstimates,
  getContributionRecommendations,
} from "actions/contributionActions";
import { contributionConstants, userConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import ContributionSelector from "components/ContributionSelector";
import { getIraAccountIdSelector } from "store/selectors/user";
import {
  getEmployerLinkRequests,
  getLinkedEmployers,
} from "actions/userActions";
import { activeInvestmentAccountsSelector } from "store/selectors/accounts";
import { payrollContributionAccountIdSelector } from "store/selectors/accounts";
import type { RootState } from "types/state";
import type { EmployerLink } from "./contributionTypes";

// Mirrors `recommendedContributionType` from statics/propTypes and the
// matching interface in components/ContributionSelector (recommended/other
// treated as populated by the time the selector renders).
interface RecommendedContribution {
  recommended: number;
  other: number[];
  isMaxContributionOption?: boolean;
}

const actions = [
  contributionConstants.ONBOARDING_PAYCHECK_CONTRIBUTION,
  contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS,
  contributionConstants.PAYCHECK_CONTRIBUTION,
  contributionConstants.EDIT_PAYCHECK_CONTRIBUTION,
  userConstants.USER_STATE,
  userConstants.USER_EMPLOYER_LINK_REQUESTS,
  userConstants.USER_EMPLOYER_LINK,
];
const errorSelector = createErrorSelector(actions);
const loadingSelector = createLoadingSelector(
  contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS
);
const isSubmitting = createLoadingSelector(actions);

const mapStateToProps = (state: RootState) => {
  const { dailyMax, perPayPeriodMax, recommendations } = state.contribution as {
    dailyMax: number | null;
    perPayPeriodMax: number | null;
    recommendations: {
      scheduleFrequency?: string;
      daily?: RecommendedContribution;
      perPayPeriod?: RecommendedContribution;
    };
  };
  const { scheduleFrequency } = recommendations;

  return {
    dailyMax,
    perPayPeriodMax,
    dailyRecommendations: recommendations.daily,
    perPayPeriodRecommendations: recommendations.perPayPeriod,
    error: errorSelector(state),
    isFetching: loadingSelector(state),
    isSubmitting: isSubmitting(state),
    accountId: getIraAccountIdSelector(state),
    scheduleFrequency,
    accounts: activeInvestmentAccountsSelector(state),
    payrollContributionAccountId: payrollContributionAccountIdSelector(state),
  };
};

const mapDispatchToProps = {
  getContributionRecommendations,
  editPaycheckContributionSource,
  getLinkedEmployers,
  getEmployerLinkRequests,
  addPaycheckContributionSource,

  getContributionAbilityAndEstimates,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onClose: () => void;
  // propType was optional and the parent omits it (forwarded to
  // ContributionSelector's optional onCancel)
  onCancel?: () => void;
  payrollPeriod?: string;
  employerLink: EmployerLink;
  isEditing?: boolean;
  currentContributionAmount?: number;
  showContributionDisclaimer?: boolean;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  isLoading: boolean;
}

class AddContribution extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
    };
  }

  componentDidMount() {
    void this.props.getContributionRecommendations(
      this.props.client as ApolloClient<object>,
      {
        // group is optional on the shared EmployerLink; original accessed it
        // unguarded, so `!` preserves the throw-on-undefined behavior.
        groupId: this.props.employerLink.group!.id,
      }
    );
  }

  _submitContribution = (
    amount: number,
    isDailyCustom: boolean,
    accountId: string
  ) => {
    // if contribution is empty they aren't editing an existing contribution
    const isEditing =
      this.props.isEditing && !isEmpty(this.props.employerLink.contribution);
    if (isEditing) {
      this.setState({ isLoading: true });
      void this.props
        .editPaycheckContributionSource(
          this.props.client as ApolloClient<object>,
          {
            amount,
            // contribution.id is optional on the shared EmployerLink; original
            // treated it as a definite string, so `!` preserves that.
            contributionId: this.props.employerLink.contribution!.id!,
            isDailyCustom,
            accountId,
          }
        )
        .then(async () => {
          await Promise.all([
            this.props.getLinkedEmployers(
              this.props.client as ApolloClient<object>
            ),
            this.props.getEmployerLinkRequests(
              this.props.client as ApolloClient<object>
            ),
            this.props.getContributionAbilityAndEstimates(
              this.props.client as ApolloClient<object>
            ),
          ]);
          if (!this.props.error) {
            this.props.onClose();
            toast.success("Successfully updated contribution");
          }
          this.setState({ isLoading: false });
        })
        .finally(() => this.setState({ isLoading: false }));
      return;
    }
    void this.props
      .addPaycheckContributionSource(
        this.props.client as ApolloClient<object>,
        {
          amount,
          isDailyCustom,
          // group is optional on the shared EmployerLink; preserve unguarded
          // access with `!`.
          groupId: this.props.employerLink.group!.id,
          accountId,
        }
      )
      .then(async () => {
        if (!this.props.error) {
          await this.props.getLinkedEmployers(
            this.props.client as ApolloClient<object>
          );
          await this.props.getEmployerLinkRequests(
            this.props.client as ApolloClient<object>
          );
          await this.props.getContributionAbilityAndEstimates(
            this.props.client as ApolloClient<object>
          );
          this.props.onClose();
          toast.success("Successfully added contribution");
        }
      });
  };

  render() {
    const isFetching =
      this.props.isFetching ||
      isEmpty(this.props.perPayPeriodRecommendations) ||
      this.state.isLoading;

    return (
      <>
        <Modal.Header closeButton>
          <h3 className="modal-title">
            How much do you want to invest to your IRA from your employer
            account?
          </h3>
          <p className="modal-subtitle">
            This is a long-term investment – you can put a little bit in each
            time you’re paid so that your money can start working for you. You
            can always change this at any time.
          </p>
        </Modal.Header>
        <Modal.Body>
          {isFetching && <IconSpinner centered />}
          {!isFetching && (
            <div className="contribution-selection">
              <ContributionSelector
                // selector returns number | null; pass through unchanged
                // @ts-expect-error null preserved (was PropTypes.number)
                dailyMax={this.props.dailyMax}
                // @ts-expect-error null preserved (was PropTypes.number)
                perPayPeriodMax={this.props.perPayPeriodMax}
                dailyRecommendations={this.props.dailyRecommendations}
                perPayPeriodRecommendations={
                  this.props.perPayPeriodRecommendations
                }
                error={this.props.error}
                onSubmit={this._submitContribution}
                isSubmitting={this.props.isSubmitting}
                // onCancel: optional callback the parent may omit; forwarded
                // through unchanged (runtime no-op when ContributionSelector
                // ignores it).
                onCancel={this.props.onCancel}
                currentContributionAmount={this.props.currentContributionAmount}
                // scheduleFrequency is string | undefined; pass through unchanged
                // @ts-expect-error frequency may be undefined (was PropTypes.string)
                schedule={{ frequency: this.props.scheduleFrequency }}
                isPaycheck={true}
                isEditing={
                  this.props.isEditing &&
                  !isEmpty(this.props.employerLink.contribution)
                }
                showAccountSelector={true}
                // IraAccount[] has optional id/accountType; selector output passed through unchanged
                // @ts-expect-error IraAccount.id/accountType are optional (Maybe)
                accounts={this.props.accounts}
                // payrollContributionAccountId/accountId are string | null | undefined;
                // pass through unchanged
                // @ts-expect-error null preserved (was PropTypes.string)
                defaultAccountId={
                  this.props.payrollContributionAccountId ||
                  this.props.accountId
                }
              />
              {this.props.showContributionDisclaimer && (
                <div className="disclaimer">
                  * Contribution change request may not go into effect until the
                  next payroll is processed, current payroll may process for
                  current amount.
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </>
    );
  }
}

export default connector(withApollo<Props>(AddContribution));
